"use client";
import { useEffect, useState } from "react";
import { getAllWebBanners, WebBanner } from "@/actions/webBanners";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Image from "next/image";

const SlideBannerSection = () => {
  const [banners, setBanners] = useState<WebBanner[]>([]);
  const [current, setCurrent] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function fetchBanners() {
      const res = await getAllWebBanners();
      if (res.success && res.banners) setBanners(res.banners);
    }
    fetchBanners();
  }, []);

  // Handle image errors
  const handleImageError = (bannerId: string) => {
    setImageErrors(prev => new Set([...prev, bannerId]));
  };

  useEffect(() => {
    async function fetchBanners() {
      const res = await getAllWebBanners();
      if (res.success && res.banners) setBanners(res.banners);
    }
    fetchBanners();
  }, []);

  // Calculate the indexes for the banners to show in the row (up to 3)
  const getVisibleBanners = () => {
    if (banners.length === 0) return [];
    if (banners.length <= 3) return banners;
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(banners[(current + i) % banners.length]);
    }
    return visible;
  };

  // Auto-scroll for multiple banners
  useEffect(() => {
    if (banners.length > 3) {
      const interval = setInterval(() => {
        setCurrent((prev) => (prev + 1) % banners.length);
      }, 4000); // Change every 4 seconds
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const nextSlide = () =>
    setCurrent((prev) => (banners.length <= 1 ? 0 : (prev + 1) % banners.length));
  const prevSlide = () =>
    setCurrent((prev) =>
      banners.length <= 1 ? 0 : (prev - 1 + banners.length) % banners.length
    );

  if (banners.length === 0) {
    return (
      <section id="clients" className="w-full min-h-screen flex flex-col items-center justify-center py-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">
            Общини използващи портала
          </h2>
          <p className="text-xl text-gray-600 mb-8">за Общински съвети</p>
          <div className="text-gray-500 text-lg">Няма банери за показване.</div>
        </div>
      </section>
    );
  }

  return (
    <section id="clients" className="w-full min-h-screen flex flex-col items-center justify-center py-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Section Title */}
      <div className="max-w-4xl mx-auto text-center px-4 mb-16">
        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">
          Общини използващи портала
        </h2>
        <p className="text-xl text-gray-600">за Общински съвети</p>
        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto mt-6 rounded-full"></div>
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4">
        {/* Desktop: up to 3 banners in a row */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {getVisibleBanners().map((banner, index) => (
            <div
              key={banner.id}
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white"
              style={{ height: "24rem" }}
            >
              {/* Background Image with error handling */}
              {!imageErrors.has(banner.id) ? (
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40">
                    <Image
                      src={`/images/${banner.image}`}
                      alt={banner.title}
                      fill
                      className="object-contain transition-transform duration-700 group-hover:scale-105"
                      style={{ objectFit: 'contain', objectPosition: 'center center' }}
                      onError={() => handleImageError(banner.id)}
                      sizes="(max-width: 768px) 128px, 160px"
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100" />
              )}
              
              {/* Light Overlay for better text visibility */}
              <div className="absolute inset-0 bg-white/10" />
              
              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-6 z-10">
                {/* Text content - Top Left */}
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 leading-tight">
                    {banner.title}
                  </h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {banner.description}
                  </p>
                </div>
                
                {/* Button - Bottom Right */}
                <div className="flex justify-end">
                  <a
                    href={banner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Посетете сайта
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: 1 banner at a time */}
        <div className="flex md:hidden w-full justify-center">
          <div
            className="group relative overflow-hidden rounded-2xl shadow-xl bg-white w-full max-w-sm"
            style={{ height: "28rem" }}
          >
            {/* Background Image with error handling */}
            {banners.length > 0 && !imageErrors.has(banners[current].id) ? (
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="relative w-28 h-28 sm:w-32 sm:h-32">
                  <Image
                    src={`/images/${banners[current].image}`}
                    alt={banners[current].title}
                    fill
                    className="object-contain transition-transform duration-700"
                    style={{ objectFit: 'contain', objectPosition: 'center center' }}
                    onError={() => handleImageError(banners[current].id)}
                    sizes="(max-width: 768px) 112px, 128px"
                  />
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100" />
            )}
            
            {/* Light Overlay for better text visibility */}
            <div className="absolute inset-0 bg-white/10" />
            
            {/* Content */}
            <div className="relative h-full flex flex-col justify-between p-6 z-10">
              {/* Text content - Top Left */}
              <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 leading-tight">
                  {banners[current].title}
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {banners[current].description}
                </p>
              </div>
              
              {/* Button - Bottom Right */}
              <div className="flex justify-end">
                <a
                  href={banners[current].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Посетете сайта
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows - Desktop */}
        {banners.length > 3 && (
          <>
            <button
              onClick={prevSlide}
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-10"
              aria-label="Предишен"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-10"
              aria-label="Следващ"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Navigation Arrows - Mobile */}
        {banners.length > 1 && (
          <div className="md:hidden flex justify-center space-x-4 mt-8">
            <button
              onClick={prevSlide}
              className="flex items-center justify-center w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
              aria-label="Предишен"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="flex items-center justify-center w-12 h-12 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
              aria-label="Следващ"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      {/* Dots Indicator - Enhanced for many banners */}
      {banners.length > 1 && (
        <div className="flex justify-center space-x-2 mt-8 flex-wrap max-w-md mx-auto">
          {banners.length <= 10 ? (
            // Show all dots for 10 or fewer banners
            banners.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  current === idx 
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-125" 
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Към банер ${idx + 1}`}
              />
            ))
          ) : (
            // Show condensed dots with current position indicator for many banners
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-medium">
                {current + 1} / {banners.length}
              </span>
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, banners.length) }, (_, idx) => {
                  const dotIndex = Math.floor((current / banners.length) * 5);
                  return (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        idx === dotIndex
                          ? "bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-125"
                          : "bg-gray-300"
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default SlideBannerSection;